import { useCallback, useState, useRef } from "react";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { useCSVEnrichmentHandlers } from "../../../../services/actions/csv-enrichment/useCSVEnrichmentHandlers";
import { ReactComponent as CsvEnrichmentImage } from "../../../../assets/img/enrichment/csv_enrichment.svg";
import Text from "../../../../components/Text";
import styled from "styled-components";
import { styles } from "../../../../styles/themes/style";
import ActionText from "../../../../components/ActionText";
import Row from "../../../../components/Row";
import Button from "../../../../components/Button";
import SvgFragment from "../../../../fragments/SvgFragment";
import { useExternalHandlers } from "../../../../services/actions/navigation/useExternalHandlers";
import Confetti from "react-confetti";

const CsvEnrichmentForm = () => {
  const {
    showSpinner,
    hideSpinner,
    setMessageModal,
    hideModal,
    setCalculateEnrichmentModalHandler,
  } = useAppHandlers();
  const { parseCsvHandler } = useCSVEnrichmentHandlers();
  const {
    openCSVEnrichmentUpload,
    openCSVEnrichmentRequirements,
    openCSVEnrichmentSample,
  } = useExternalHandlers();
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const calculateEnrichmentHandler = useCallback(async () => {
    console.log("calculateEnrichmentHandler");
  }, []);

  const handleFileUpload = async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("csv", file);

    try {
      showSpinner();
      const response = await parseCsvHandler(formData);
      if (response && response.success === false) {
        setMessageModal({
          headingText: "Error",
          confirmBtnTitle: "Ok",
          descriptionText: response.message || "Error processing file",
          onConfirmHandler: () => hideModal(),
        });
        return;
      } else {
        setShowConfetti(false);
        setCalculateEnrichmentModalHandler(
          calculateEnrichmentHandler,
          () => {},
          undefined,
          undefined,
          response?.data?.results,
          false,
          false,
          false,
          false,
          showEnrichmentConfetti
        );
      }
    } catch (error) {
      setMessageModal({
        headingText: "Error",
        confirmBtnTitle: "Ok",
        descriptionText: "Error processing file",
        onConfirmHandler: () => hideModal(),
      });
      console.error(error);
    } finally {
      hideSpinner();
    }
  };

  const showEnrichmentConfetti = () => {
    setShowConfetti(true);
  };

  const openLink = (url: string) => {
    window.open(url, "_blank");
  };

  const processFile = async () => {
    if (file) {
      await handleFileUpload();
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }, 0);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        margin: "0 auto",
        padding: "20px",
        background: "#fff",
      }}
    >
      {showConfetti && (
        <>
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            numberOfPieces={1800}
            recycle={false}
            gravity={0.2}
            initialVelocityY={25}
            initialVelocityX={20}
            confettiSource={{ x: 0, y: window.innerHeight - 1, w: 0, h: 0 }}
            run={true}
          />
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            numberOfPieces={1800}
            recycle={false}
            gravity={0.2}
            initialVelocityY={25}
            initialVelocityX={20}
            confettiSource={{
              x: window.innerWidth,
              y: window.innerHeight - 1,
              w: 0,
              h: 0,
            }}
            run={true}
          />
        </>
      )}
      <div style={{ flex: 1 }}>
        <div
          style={{
            marginBottom: "20px",
            gap: "16px",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              gap: "16px",
              width: "470px",
              minHeight: "496px",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <Text $heading3>{"Enrich your contact list"}</Text>
            <CsvEnrichmentImage />
            <div
              style={{
                gap: "16px",
                width: "350px",
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <StyledText>
                {
                  "Just upload a CSV with LinkedIn Profile URLs, and we’ll do the rest. Get verified "
                }
                <StyledBoldText $bold>
                  {`business emails, personal emails,`}
                </StyledBoldText>
                {" and "}
                <StyledBoldText $bold>{`phone numbers`}</StyledBoldText>
                {" in one go."}
              </StyledText>
              <div
                style={{
                  padding: "8px",
                  marginLeft: "28px",
                  marginRight: "28px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#EEF3FF",
                }}
              >
                <CenteredTextDark $paragraph3>
                  {
                    "You can import up to 15,000 rows at a time. The maximum file size limit is 5MB in a .CSV format. Download Sample .CSV"
                  }{" "}
                  <ActionTextLink
                    onClickHandler={() => openCSVEnrichmentSample()}
                  >
                    {"here"}
                  </ActionTextLink>
                </CenteredTextDark>
              </div>

              {!file && (
                <div style={{ marginBottom: "15px" }}>
                  <Label htmlFor="csv-file-input">
                    <SvgFragment type="import" />
                    Import CSV
                  </Label>
                  <span style={{ marginLeft: "10px" }}>{""}</span>
                </div>
              )}

              {file && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "right",
                    gap: "10px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    width: "320px",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #DAE0E8",
                      borderRadius: "4px",
                      padding: "4px",
                      alignContent: "center",
                      width: "100%",
                    }}
                  >
                    <Text $paragraph3>{file.name}</Text>
                  </div>
                  <Button
                    $type="light-blue"
                    $mediumButton
                    onClickHandler={() => fileInputRef.current?.click()}
                  >
                    Change
                  </Button>
                </div>
              )}

              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={async (e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  setFile(selectedFile);
                }}
                style={{ display: "none" }}
              />

              <Row gap="10px">
                <ActionTextLink
                  onClickHandler={() => openCSVEnrichmentUpload()}
                >
                  {"How the CSV enrichment works"}
                </ActionTextLink>
                <Divider />
                <ActionTextLink
                  onClickHandler={() => openCSVEnrichmentRequirements()}
                >
                  {"CSV file requirements"}
                </ActionTextLink>
              </Row>

              {file && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "right",
                    gap: "10px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    width: "320px",
                  }}
                >
                  <Button
                    $mediumButton
                    $type="cancel"
                    onClickHandler={() => {
                      setFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                  >
                    {"Cancel"}
                  </Button>
                  <Button
                    $type="blue"
                    $mediumButton
                    onClickHandler={processFile}
                  >
                    {"Continue"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CenteredTextLight = styled(Text)`
  display: inline-block;
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
  color: ${styles.colors.black400};
`;

const CenteredTextDark = styled(Text)`
  display: inline;
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: center;
  color: ${styles.colors.black600};
`;

const ActionTextLink = styled(ActionText)`
  display: inline-block;
  span {
    font-family: Nunito Sans;
    font-weight: 700;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-align: center;
    color: ${styles.colors.primary600};
  }
`;

const Divider = styled.div`
  width: 2px;
  height: 16px;
  background: ${styles.colors.black200};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${styles.colors.primary600};
  color: white;
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  line-height: 20px;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: bold;

  & svg {
    path {
      fill: white;
    }
  }

  &:hover {
    background: #7a89fc;
  }
`;
const StyledText = styled(Text)`
  display: inline;
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
`;
const StyledBoldText = styled(Text)`
  display: inline;
  font-weight: bold;
  text-align: center;
  font-family: Nunito Sans;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
`;
export default CsvEnrichmentForm;
